import { escape as escapeHTML } from "html-escaper";
import Mousetrap from "mousetrap";
import queueMicrotask from "queue-microtask";
import * as React from "react";
import { throttleTime } from "rxjs";
import { cloestElement, convertPositionFromDOM, lookupEditorElement, readSelectionFromDOM, writeSelectionToDOM, } from "./dom-utils";
import { assert, contentToString, isJustAfterEmptyElementWithBoundary, isJustBeforeEmptyElementWithBoundary, isJustInsideEmptyElementWithBoundary, liftCursor, newSelection, sliceContent, } from "./utils";
class GlobalInputDelegate {
    constructor() {
        this.clients = new Map();
        this.isPointerDown = false;
        // private needExplicitRecover = false;
        this.composingClient = null;
        this.activeClient = null;
        this.activeChangeSubscriptions = new WeakMap();
        this.mousetrap = new Mousetrap();
        this.ignoreCommittedDomMutations = false;
        this.domMutationBuffer = [];
        this.dmObserver = new MutationObserver((records) => {
            if (this.ignoreCommittedDomMutations) {
                this.dmObserver.takeRecords();
                this.ignoreCommittedDomMutations = false;
                return;
            }
            this.domMutationBuffer.push(...records, ...this.dmObserver.takeRecords());
            this.checkRestoreDom();
        });
        this.handleCompositionStart = (event) => {
            const client = this.lookupClient(event);
            if (client === null) {
                return;
            }
            client.performChange((ctx) => {
                ctx.delete();
            });
            this.composingClient = client;
            this.composingClient.setComposing(true);
        };
        this.handleCompositionEnd = (event) => {
            if (this.composingClient === null) {
                return;
            }
            const client = this.lookupClient(event);
            this.composingClient.setComposing(false);
            this.composingClient = null;
            if (client !== null) {
                queueMicrotask(() => {
                    this.checkRestoreDom();
                    client.inputInsert(event.data, "composition");
                });
            }
            // this.needExplicitRecover = false;
        };
        this.handleBeforeInput = (event) => {
            const client = this.lookupClient(event);
            if (client === null)
                return;
            event.preventDefault();
            switch (event.inputType) {
                case "insertText": {
                    // NOTE: Sometimes browser doesn't fire compositionend event,
                    // but fire insertText beforeinput event
                    // So set isComposing = false here
                    if (this.composingClient !== null) {
                        this.composingClient.setComposing(false);
                        this.composingClient = null;
                    }
                    client.inputInsert(event.data, "input");
                    break;
                }
                case "deleteWordBackward":
                case "deleteWordForward":
                case "deleteSoftLineBackward":
                case "deleteSoftLineForward":
                case "deleteEntireSoftLine":
                case "deleteHardLineBackward":
                case "deleteHardLineForward":
                case "deleteByCut":
                case "deleteContent":
                case "deleteContentBackward":
                case "deleteContentForward": {
                    const [range] = event.getTargetRanges();
                    const startPos = convertPositionFromDOM([
                        range.startContainer,
                        range.startOffset,
                    ]);
                    const endPos = convertPositionFromDOM([
                        range.endContainer,
                        range.endOffset,
                    ]);
                    assert(startPos[0] === endPos[0]);
                    let offset = startPos[1];
                    let endOffset = endPos[1];
                    if (offset === endOffset) {
                        if (event.inputType === "deleteContentBackward") {
                            if (offset !== 0) {
                                if (isJustAfterEmptyElementWithBoundary(client.content, offset)) {
                                    offset -= 2;
                                }
                                else if (isJustInsideEmptyElementWithBoundary(client.content, offset)) {
                                    offset -= 1;
                                    endOffset += 1;
                                }
                                else {
                                    offset = Math.max(0, offset - 1);
                                }
                            }
                        }
                        else if (event.inputType === "deleteContentForward") {
                            if (offset !== client.content.length) {
                                if (isJustBeforeEmptyElementWithBoundary(client.content, offset)) {
                                    endOffset += 2;
                                }
                                else if (isJustInsideEmptyElementWithBoundary(client.content, offset)) {
                                    offset -= 1;
                                    endOffset += 1;
                                }
                                else {
                                    endOffset = Math.min(client.content.length, endOffset + 1);
                                }
                            }
                        }
                    }
                    endOffset = liftCursor(client.content, endOffset, "forward");
                    client.inputDelete(offset, endOffset);
                    break;
                }
            }
        };
        this.handleCopy = (event) => {
            const client = this.lookupClient(event);
            if (client === null)
                return;
            event.preventDefault();
            if (client.selection === null)
                return;
            const content = sliceContent(client.content, client.selection.offset, client.selection.endOffset);
            const clipboardData = event.clipboardData;
            clipboardData.setData("text/html", `<div data-inlined-content="${escapeHTML(JSON.stringify(content))}"></div>`);
            clipboardData.setData("text/plain", contentToString(content));
        };
        this.handleCut = (event) => {
            const client = this.lookupClient(event);
            if (client === null)
                return;
            event.preventDefault();
            if (client.selection === null)
                return;
            const content = sliceContent(client.content, client.selection.offset, client.selection.endOffset);
            event.clipboardData.setData("text/html", `<div data-inlined-content="${escapeHTML(JSON.stringify(content))}"></div>`);
            client.inputDelete(client.selection.offset, client.selection.endOffset);
        };
        this.handlePaste = (event) => {
            const client = this.lookupClient(event);
            if (client === null)
                return;
            event.preventDefault();
            const data = event.clipboardData.getData("text/html");
            let content = null;
            if (data !== null) {
                const div = document.createElement("div");
                div.innerHTML = data;
                const div2 = div.querySelector(`[data-inlined-content]`);
                if (div2 !== null) {
                    content = JSON.parse(div2.getAttribute("data-inlined-content"));
                }
            }
            if (content === null) {
                content = event.clipboardData.getData("text/plain");
            }
            client.inputInsert(content, "paste");
        };
        this.handlePointerDown = () => {
            this.isPointerDown = true;
        };
        this.handlePointerUp = () => {
            this.isPointerDown = false;
        };
        this.handleSelectionChange = () => {
            var _a;
            const readResult = readSelectionFromDOM();
            let client;
            let selection;
            if (readResult === null) {
                client = null;
                selection = null;
            }
            else {
                client = (_a = this.clients.get(readResult[0])) !== null && _a !== void 0 ? _a : null;
                selection = readResult[1];
            }
            if (this.activeClient !== null) {
                if (this.activeClient !== client) {
                    this.activeClient.inputSelection(null);
                }
            }
            this.activeClient = client;
            if (client !== null) {
                if (!client.isComposing) {
                    client.inputSelection(selection);
                }
            }
        };
        this.handleDoubleClick = (event) => {
            if (event.detail !== 2)
                return;
            const element = cloestElement(event.target);
            if (element == null)
                return;
            const atom = element.closest(`[data-tr40owj][data-length="1"]`);
            if (atom != null && atom.parentNode != null) {
                const range = new Range();
                range.setStartBefore(atom);
                const pos = convertPositionFromDOM([
                    range.startContainer,
                    range.startOffset,
                ]);
                if (pos == null)
                    return;
                const [clientKey, offset] = pos;
                const client = this.clients.get(clientKey);
                if (client == null)
                    return;
                client.inputSelection(newSelection(offset, offset + 1));
                event.preventDefault();
            }
        };
        this.handleLeftOrRight = (event, key) => {
            if (event.defaultPrevented)
                return;
            if (this.activeClient === null)
                return;
            if (this.activeClient.selection === null)
                return;
            if (!this.activeClient.selection.isCollapsed) {
                this.activeClient.performChange((ctx) => {
                    ctx.collapse(key === "left" ? "start" : "end");
                });
            }
            else {
                this.activeClient.performChange((ctx) => {
                    ctx.move(key === "left" ? -1 : 1).collapse("focus");
                });
            }
            event.preventDefault();
        };
        this.handleShiftLeftOrRight = (event, key) => {
            if (event.defaultPrevented)
                return;
            if (this.activeClient === null)
                return;
            if (this.activeClient.selection === null)
                return;
            // TODO: Across empty element
            this.activeClient.performChange((ctx) => {
                ctx.move(key === "shift+left" ? -1 : 1);
            });
            event.preventDefault();
        };
    }
    checkRestoreDom() {
        var _a;
        if (this.composingClient != null)
            return;
        this.ignoreCommittedDomMutations = true;
        this.domMutationBuffer.push(...this.dmObserver.takeRecords());
        const records = this.domMutationBuffer;
        const hasRecords = records.length > 0;
        this.domMutationBuffer = [];
        while (records.length > 0) {
            const record = records.pop();
            switch (record.type) {
                case "characterData": {
                    record.target.nodeValue = record.oldValue;
                    break;
                }
                case "childList": {
                    try {
                        for (const node of Array.from(record.removedNodes)) {
                            record.target.insertBefore(node, record.nextSibling);
                        }
                        for (const node of Array.from(record.addedNodes)) {
                            (_a = node.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(node);
                        }
                    }
                    catch (_b) {
                        //
                    }
                    break;
                }
            }
        }
        this.ignoreCommittedDomMutations = false;
        this.dmObserver.takeRecords();
        if (this.activeClient != null && hasRecords) {
            this.renderSelection(this.activeClient.editorKey, this.activeClient.selection);
        }
    }
    renderSelection(editorKey, selection) {
        var _a;
        const client = (_a = this.clients.get(editorKey)) !== null && _a !== void 0 ? _a : null;
        // NOTE: Otherwise the user select interaction is break.
        if (this.isPointerDown)
            return;
        // Sometimes this.activeClient == null && client.active
        // if (this.activeClient === client) {
        if (client !== null && client.active) {
            if (selection !== null) {
                const selectionRevision = client.getSelectionRevision(selection);
                if (client.revision === selectionRevision && client.isContentRendered) {
                    writeSelectionToDOM([editorKey, selection]);
                }
            }
            else {
                writeSelectionToDOM([editorKey, newSelection(0, 0)]);
            }
        }
    }
    setupListeners() {
        document.addEventListener("compositionstart", this.handleCompositionStart);
        document.addEventListener("compositionend", this.handleCompositionEnd);
        document.addEventListener("beforeinput", this.handleBeforeInput);
        document.addEventListener("copy", this.handleCopy);
        document.addEventListener("cut", this.handleCut);
        document.addEventListener("paste", this.handlePaste);
        document.addEventListener("pointerdown", this.handlePointerDown, {
            capture: true,
        });
        document.addEventListener("pointerup", this.handlePointerUp, {
            capture: true,
        });
        document.addEventListener("click", this.handleDoubleClick);
        document.addEventListener("selectionchange", this.handleSelectionChange);
        this.mousetrap.bind(["left", "right"], this.handleLeftOrRight);
        this.mousetrap.bind(["shift+left", "shift+right"], this.handleShiftLeftOrRight);
        this.mousetrap.stopCallback = () => false;
    }
    teardownListeners() {
        document.removeEventListener("compositionstart", this.handleCompositionStart);
        document.removeEventListener("compositionend", this.handleCompositionEnd);
        document.removeEventListener("beforeinput", this.handleBeforeInput);
        document.removeEventListener("copy", this.handleCopy);
        document.removeEventListener("cut", this.handleCut);
        document.removeEventListener("paste", this.handlePaste);
        document.removeEventListener("pointerdown", this.handlePointerDown, {
            capture: true,
        });
        document.removeEventListener("pointerup", this.handlePointerUp, {
            capture: true,
        });
        document.removeEventListener("click", this.handleDoubleClick);
        document.removeEventListener("selectionchange", this.handleSelectionChange);
        this.mousetrap.reset();
    }
    lookupClient(event) {
        var _a;
        const editorElement = lookupEditorElement(event.target);
        if (editorElement === null)
            return null;
        const editorKey = editorElement.getAttribute("data-editor");
        return (_a = this.clients.get(editorKey)) !== null && _a !== void 0 ? _a : null;
    }
    addClient(editorKey, client) {
        this.dmObserver.observe(client.editorElement, {
            subtree: true,
            childList: true,
            characterData: true,
            characterDataOldValue: true,
        });
        const oldSize = this.clients.size;
        this.clients.set(editorKey, client);
        if (oldSize === 0) {
            this.setupListeners();
        }
        if (this.clients.size > oldSize) {
            this.activeChangeSubscriptions.set(client, client.onActiveChange.subscribe(() => {
                if (client.active) {
                    this.activeClient = client;
                }
                else {
                    if (this.activeClient === client) {
                        this.activeClient = null;
                    }
                }
            }));
        }
    }
    removeClient(editorKey) {
        const oldSize = this.clients.size;
        this.clients.delete(editorKey);
        if (oldSize > 0 && this.clients.size === 0) {
            this.teardownListeners();
        }
    }
    onReactCommit() {
        this.dmObserver.takeRecords();
        this.ignoreCommittedDomMutations = true;
        queueMicrotask(() => {
            this.ignoreCommittedDomMutations = false;
        });
    }
}
export const globalInputDelegate = new GlobalInputDelegate();
const LOCK = Symbol();
export const useInput = (model0) => {
    const model = model0;
    const lockRef = React.useRef({});
    model[LOCK] = lockRef.current;
    React.useEffect(() => {
        globalInputDelegate.addClient(model.editorKey, model);
        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            if (model[LOCK] === lockRef.current) {
                globalInputDelegate.removeClient(model.editorKey);
            }
        };
    }, [model]);
    React.useLayoutEffect(() => {
        const subscription1 = model.onSelectionChange
            .pipe(throttleTime(0))
            .subscribe(() => {
            if (model.isContentRendered) {
                globalInputDelegate.renderSelection(model.editorKey, model.selection);
            }
        });
        const subscription2 = model.onContentRendered.subscribe(() => {
            globalInputDelegate.renderSelection(model.editorKey, model.selection);
        });
        return () => {
            subscription1.unsubscribe();
            subscription2.unsubscribe();
        };
    }, [model]);
};
